import { useCallback, useEffect, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { getUploadErrorMessage, postUploadFormDataWithRetry } from '../../lib/uploadRetry';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './dialog';
import { Button } from './button';
import {
  Upload,
  Loader2,
  RotateCw,
  RotateCcw,
  ZoomIn,
  ZoomOut,
  Image as ImageIcon,
  Crop as CropIcon,
  X,
  Check,
} from 'lucide-react';

const API_URL = window.__BACKEND_URL__;
const API_BASE = API_URL || window.location.origin;

const MAX_UPLOAD_MB = {
  avatar: 8,
  profile: 15,
  headshot: 15,
  gallery: 15,
  logo: 8,
  cover: 10,
  banner: 10,
  'news-cover': 15,
  flyer: 15,
  event: 15,
  messenger: 10,
  free: 15,
  default: 15,
};

const ACCEPTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
];

const OUTPUT_POLICIES = {
  profile: { outputMaxEdge: 2400, mimeType: 'image/jpeg', quality: 0.94 },
  headshot: { outputMaxEdge: 2400, mimeType: 'image/jpeg', quality: 0.94 },
  avatar: { outputMaxEdge: 800, mimeType: 'image/jpeg', quality: 0.92 },
  logo: { outputMaxEdge: 1600, mimeType: 'image/png', quality: undefined },
  cover: { outputMaxEdge: 2560, mimeType: 'image/jpeg', quality: 0.93 },
  'news-cover': { outputMaxEdge: 3000, mimeType: 'image/jpeg', quality: 0.94 },
  gallery: { outputMaxEdge: 2400, mimeType: 'image/jpeg', quality: 0.93 },
  flyer: { outputMaxEdge: 3000, mimeType: 'image/jpeg', quality: 0.95 },
  event: { outputMaxEdge: 3000, mimeType: 'image/jpeg', quality: 0.95 },
  free: { outputMaxEdge: 2400, mimeType: 'image/jpeg', quality: 0.93 },
  default: { outputMaxEdge: 2400, mimeType: 'image/jpeg', quality: 0.93 },
};

function getOutputPolicy(type = 'profile') {
  return OUTPUT_POLICIES[type] || OUTPUT_POLICIES.default;
}

function getMaxUploadMb(type = 'profile') {
  return MAX_UPLOAD_MB[type] || MAX_UPLOAD_MB.default;
}

function getUploadFilename(policy) {
  if (policy.mimeType === 'image/png') return 'upload.png';
  if (policy.mimeType === 'image/webp') return 'upload.webp';
  return 'upload.jpg';
}

function encodeCanvas(canvas, mimeType = 'image/jpeg', quality = 0.93) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Failed to encode image'))),
      mimeType,
      mimeType === 'image/jpeg' || mimeType === 'image/webp' ? quality : undefined
    );
  });
}

function isLocalImageSrc(url) {
  return typeof url === 'string' && (url.startsWith('data:') || url.startsWith('blob:'));
}

function getAbsoluteImageSrc(url) {
  if (!url || typeof url !== 'string') return url;
  if (isLocalImageSrc(url)) return url;
  try {
    return new URL(url, API_BASE).toString();
  } catch {
    return url;
  }
}

function getImageCrossOrigin(src) {
  if (!src || isLocalImageSrc(src)) return undefined;
  try {
    const srcUrl = new URL(src, window.location.origin);
    const apiUrl = new URL(API_BASE);
    if (srcUrl.origin === apiUrl.origin || srcUrl.pathname.includes('/api/upload/proxy-image')) {
      return 'use-credentials';
    }
  } catch {
    return 'use-credentials';
  }
  return undefined;
}

function getCanvasSafeSrc(url) {
  if (!url || typeof url !== 'string') return url;
  if (isLocalImageSrc(url)) return url;

  const absoluteUrl = getAbsoluteImageSrc(url);
  try {
    const parsed = new URL(absoluteUrl);
    const api = new URL(API_BASE);
    const proxyBase = `${api.origin}/api/upload/proxy-image`;
    if (absoluteUrl.startsWith(proxyBase)) return absoluteUrl;
    if (parsed.origin === api.origin && parsed.pathname.startsWith('/api/upload/')) {
      return absoluteUrl;
    }
    return `${proxyBase}?url=${encodeURIComponent(absoluteUrl)}`;
  } catch {
    return `${API_BASE}/api/upload/proxy-image?url=${encodeURIComponent(absoluteUrl)}`;
  }
}

async function getFullImageCompressedBlob(src, outputPolicy = OUTPUT_POLICIES.default) {
  const {
    outputMaxEdge = 2400,
    mimeType = 'image/jpeg',
    quality = 0.93,
  } = outputPolicy;

  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    const crossOrigin = getImageCrossOrigin(src);
    if (crossOrigin) img.crossOrigin = crossOrigin;
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

  let targetW = image.width;
  let targetH = image.height;
  const maxEdge = Math.max(targetW, targetH);
  if (maxEdge > outputMaxEdge) {
    const scale = outputMaxEdge / maxEdge;
    targetW = Math.round(targetW * scale);
    targetH = Math.round(targetH * scale);
  }

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image, 0, 0, targetW, targetH);
  return encodeCanvas(canvas, mimeType, quality);
}

async function getCroppedBlob(src, pixelCrop, rotation = 0, outputPolicy = OUTPUT_POLICIES.default) {
  const {
    outputMaxEdge = 2400,
    mimeType = 'image/jpeg',
    quality = 0.93,
  } = outputPolicy;

  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    const crossOrigin = getImageCrossOrigin(src);
    if (crossOrigin) img.crossOrigin = crossOrigin;
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

  const rad = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rad));
  const cos = Math.abs(Math.cos(rad));
  const bBoxW = image.width * cos + image.height * sin;
  const bBoxH = image.width * sin + image.height * cos;

  const boundingCanvas = document.createElement('canvas');
  boundingCanvas.width = Math.ceil(bBoxW);
  boundingCanvas.height = Math.ceil(bBoxH);
  const bCtx = boundingCanvas.getContext('2d');
  bCtx.imageSmoothingEnabled = true;
  bCtx.imageSmoothingQuality = 'high';
  bCtx.translate(boundingCanvas.width / 2, boundingCanvas.height / 2);
  bCtx.rotate(rad);
  bCtx.drawImage(image, -image.width / 2, -image.height / 2);

  let targetW = pixelCrop.width;
  let targetH = pixelCrop.height;
  const maxEdge = Math.max(targetW, targetH);
  if (maxEdge > outputMaxEdge) {
    const scale = outputMaxEdge / maxEdge;
    targetW = Math.round(targetW * scale);
    targetH = Math.round(targetH * scale);
  }

  const outCanvas = document.createElement('canvas');
  outCanvas.width = targetW;
  outCanvas.height = targetH;
  const ctx = outCanvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    boundingCanvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    targetW,
    targetH
  );
  return encodeCanvas(outCanvas, mimeType, quality);
}

function readBlobAsDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function fileExtension(file) {
  const name = file?.name || '';
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot).toLowerCase() : '';
}

function isHeifFile(file) {
  const type = (file?.type || '').toLowerCase();
  const ext = fileExtension(file);
  return type === 'image/heic' || type === 'image/heif' || ext === '.heic' || ext === '.heif';
}

function isAcceptedImageFile(file) {
  const type = (file?.type || '').toLowerCase();
  const ext = fileExtension(file);
  return ACCEPTED_TYPES.includes(type) || ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif'].includes(ext);
}

const ASPECT_PRESETS = {
  profile: [{ label: 'Square', value: 1 }],
  headshot: [{ label: 'Square', value: 1 }],
  logo: [
    { label: 'Fit (no crop)', value: null },
    { label: 'Banner 3:1', value: 3 / 1 },
    { label: 'Wide 16:9', value: 16 / 9 },
    { label: 'Square 1:1', value: 1 },
  ],
  avatar: [{ label: 'Square', value: 1 }],
  cover: [
    { label: 'Banner 3:1', value: 3 / 1 },
    { label: 'Wide 16:9', value: 16 / 9 },
  ],
  'news-cover': [
    { label: 'Wide 16:9', value: 16 / 9 },
    { label: 'Banner 3:1', value: 3 / 1 },
  ],
  gallery: [
    { label: 'Portrait 4:5', value: 4 / 5 },
    { label: '1:1', value: 1 },
    { label: '4:3', value: 4 / 3 },
    { label: 'Free', value: null },
  ],
  flyer: [
    { label: 'Fit (no crop)', value: null },
    { label: 'Portrait 4:5', value: 4 / 5 },
    { label: 'Portrait 3:4', value: 3 / 4 },
    { label: '16:9', value: 16 / 9 },
  ],
  event: [
    { label: 'Wide 16:9', value: 16 / 9 },
  ],
  free: [
    { label: 'Free crop', value: 'free' },
    { label: 'Fit (no crop)', value: null },
    { label: '1:1', value: 1 },
    { label: '4:5', value: 4 / 5 },
    { label: '4:3', value: 4 / 3 },
    { label: '16:9', value: 16 / 9 },
  ],
};

export default function ImageEditModal({
  open,
  onOpenChange,
  initialUrl = null,
  type = 'profile',
  title = 'Edit image',
  description = 'Crop, reposition and refine before saving.',
  onSaved,
}) {
  const fileInputRef = useRef(null);
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [aspect, setAspect] = useState(() => {
    const list = ASPECT_PRESETS[type] || ASPECT_PRESETS.free;
    return list[0].value;
  });
  // Used only by the genuine Free crop mode. The user can change the
  // crop-box shape continuously instead of being restricted to presets.
  const [freeAspect, setFreeAspect] = useState(1);
  const [pixelCrop, setPixelCrop] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [cropperReady, setCropperReady] = useState(false);
  const [cropperError, setCropperError] = useState(false);
  const [fallbackAttempted, setFallbackAttempted] = useState(false);

  useEffect(() => {
    if (open) {
      setSrc(initialUrl ? getCanvasSafeSrc(initialUrl) : null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      const list = ASPECT_PRESETS[type] || ASPECT_PRESETS.free;
      setAspect(list[0].value);
      setFreeAspect(1);
      setPixelCrop(null);
      setCropperReady(false);
      setCropperError(false);
      setFallbackAttempted(false);
    }
  }, [open, initialUrl, type]);

  const aspectList = ASPECT_PRESETS[type] || ASPECT_PRESETS.free;
  const maxUploadMb = getMaxUploadMb(type);
  const activeCropAspect = aspect === 'free' ? freeAspect : aspect;
  const saveNeedsCrop = aspect !== null || rotation % 360 !== 0;
  const saveReady = Boolean(src)
    && cropperReady
    && !cropperError
    && (!saveNeedsCrop || Boolean(pixelCrop));

  const onCropComplete = useCallback((_area, areaPixels) => {
    setPixelCrop(areaPixels);
  }, []);

  const handleCropperError = useCallback(() => {
    const directSrc = initialUrl ? getAbsoluteImageSrc(initialUrl) : null;
    if (!fallbackAttempted && directSrc && directSrc !== src) {
      setFallbackAttempted(true);
      setCropperReady(false);
      setCropperError(false);
      setSrc(directSrc);
      return;
    }
    setCropperReady(false);
    setCropperError(true);
  }, [fallbackAttempted, initialUrl, src]);

  const handleFilePicked = async (file) => {
    if (!file) return;
    if (!isAcceptedImageFile(file)) {
      toast.error('Use JPG, PNG, GIF, WEBP or HEIC.');
      return;
    }
    if (file.size > maxUploadMb * 1024 * 1024) {
      toast.error(`Image too large (${maxUploadMb}MB max for this image type).`);
      return;
    }

    try {
      let dataUrl;
      if (isHeifFile(file)) {
        // Browsers do not consistently decode iPhone HEIC/HEIF into canvas.
        // Ask CCUK to normalise it to WebP in memory first; nothing is stored
        // until the user presses Save image.
        const formData = new FormData();
        formData.append('file', file, file.name || 'upload.heic');
        const prepared = await postUploadFormDataWithRetry(
          `${API_URL}/api/upload/prepare-image?kind=${encodeURIComponent(type)}`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            withCredentials: true,
            responseType: 'blob',
          }
        );
        dataUrl = await readBlobAsDataURL(prepared.data);
      } else {
        dataUrl = await readBlobAsDataURL(file);
      }

      setSrc(dataUrl);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setPixelCrop(null);
      setCropperReady(false);
      setCropperError(false);
      setFallbackAttempted(false);
    } catch (e) {
      toast.error(getUploadErrorMessage(e, 'Could not read this image.'));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFilePicked(file);
  };

  const handleSave = async () => {
    if (!src) {
      toast.error('Pick an image first.');
      return;
    }
    if (!saveReady) return;

    setUploading(true);
    try {
      const outputPolicy = getOutputPolicy(type);
      let blob;
      if (aspect === null && rotation % 360 === 0) {
        blob = await getFullImageCompressedBlob(src, outputPolicy);
      } else {
        if (!pixelCrop) return;
        blob = await getCroppedBlob(src, pixelCrop, rotation, outputPolicy);
      }

      const formData = new FormData();
      formData.append('file', blob, getUploadFilename(outputPolicy));
      const { data } = await postUploadFormDataWithRetry(
        `${API_URL}/api/upload/image?kind=${encodeURIComponent(type)}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        }
      );
      if (!data?.url) throw new Error('No URL returned');

      // Do not delete initialUrl here. The surrounding profile/form has not
      // necessarily been persisted yet. If that save is abandoned or fails,
      // deleting the previous object would leave the live DB record pointing
      // at a missing image. Physical cleanup belongs after record persistence.
      onSaved?.(data.url);
      toast.success('Image saved');
      onOpenChange?.(false);
    } catch (err) {
      toast.error(getUploadErrorMessage(err, 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const cropperMediaProps = { onError: handleCropperError };
  const crossOrigin = getImageCrossOrigin(src);
  if (crossOrigin) cropperMediaProps.crossOrigin = crossOrigin;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[94vw] max-w-xl p-0 gap-0 overflow-hidden bg-theme-surface border-theme shadow-elev-3 rounded-2xl flex flex-col max-h-[calc(100dvh-1.25rem)] sm:max-h-[calc(100dvh-2rem)]"
        data-testid="image-edit-modal"
      >
        <DialogHeader className="px-4 sm:px-5 py-3.5 border-b border-theme shrink-0">
          <div className="flex items-center gap-3 pr-8">
            <div className="h-9 w-9 rounded-lg bg-[#002FA7]/10 flex items-center justify-center">
              <CropIcon className="h-4 w-4 text-[#002FA7]" />
            </div>
            <div>
              <DialogTitle className="text-theme-text text-lg font-semibold leading-tight">
                {title}
              </DialogTitle>
              <DialogDescription className="text-theme-muted text-xs mt-0.5">
                {description}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="px-4 sm:px-5 py-3.5 space-y-3.5 flex-1 overflow-y-auto min-h-0">
          {!src && (
            <label
              htmlFor="image-edit-modal-file"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`block cursor-pointer rounded-xl border-2 border-dashed transition-all ${
                dragOver
                  ? 'border-[#002FA7] bg-[#002FA7]/5'
                  : 'border-theme hover:border-[#002FA7]/60 bg-theme/40'
              }`}
              data-testid="image-edit-dropzone"
            >
              <div className="flex flex-col items-center justify-center py-10 px-5 text-center">
                <div className="h-14 w-14 rounded-full bg-[#002FA7]/10 flex items-center justify-center mb-3">
                  <Upload className="h-6 w-6 text-[#002FA7]" />
                </div>
                <p className="text-theme-text font-medium">Drop an image here</p>
                <p className="text-theme-muted text-sm mt-1">
                  or <span className="text-[#002FA7] font-medium">browse</span> from your device
                </p>
                <p className="text-[11px] text-theme-muted mt-3">
                  JPG, PNG, GIF, WEBP or HEIC · up to {maxUploadMb}MB
                </p>
                <p className="text-[11px] text-theme-muted mt-1" data-testid="image-edit-recommended">
                  Recommended: {(() => {
                    switch (type) {
                      case 'cover': return '1500 × 500 px';
                      case 'logo': return 'up to 1600 × 1600 px (wide, square or portrait supported)';
                      case 'news-cover': return '1600 × 900 px';
                      case 'avatar': return '400 × 400 px';
                      case 'gallery': return '1080 × 1350 px';
                      case 'flyer': return '1080 × 1350 px (portrait)';
                      case 'event': return '1600 × 900 px (landscape / 16:9)';
                      default: return '800 × 800 px';
                    }
                  })()}
                </p>
              </div>
              <input
                id="image-edit-modal-file"
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,.heic,.heif"
                className="hidden"
                onChange={(e) => handleFilePicked(e.target.files?.[0])}
              />
            </label>
          )}

          {src && (
            <>
              <div
                className="relative w-full h-[170px] sm:h-[220px] lg:h-[230px] rounded-xl overflow-hidden border border-theme-subtle shadow-elev-1 bg-white dark:bg-[#050505]"
                data-testid="image-edit-canvas"
              >
                <div
                  className="absolute inset-0 opacity-80 pointer-events-none"
                  style={{
                    backgroundImage:
                      'linear-gradient(45deg, rgba(148,163,184,.22) 25%, transparent 25%), linear-gradient(-45deg, rgba(148,163,184,.22) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgba(148,163,184,.22) 75%), linear-gradient(-45deg, transparent 75%, rgba(148,163,184,.22) 75%)',
                    backgroundSize: '18px 18px',
                    backgroundPosition: '0 0, 0 9px, 9px -9px, -9px 0px',
                  }}
                />

                {!cropperReady && !cropperError && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-theme-surface/70 text-sm text-theme-muted pointer-events-none">
                    Loading image preview…
                  </div>
                )}

                {cropperError && (
                  <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-theme-surface/90 px-5 text-center">
                    <ImageIcon className="h-8 w-8 text-theme-muted mb-2" />
                    <p className="text-sm font-semibold text-theme-text">Could not load image preview</p>
                    <p className="text-xs text-theme-muted mt-1 max-w-[260px]">
                      Try Replace image, or refresh and open the editor again.
                    </p>
                  </div>
                )}

                <Cropper
                  image={src}
                  crop={crop}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={activeCropAspect || undefined}
                  onCropChange={(nextCrop) => {
                    setPixelCrop(null);
                    setCrop(nextCrop);
                  }}
                  onZoomChange={(nextZoom) => {
                    setPixelCrop(null);
                    setZoom(nextZoom);
                  }}
                  onRotationChange={(nextRotation) => {
                    setPixelCrop(null);
                    setRotation(nextRotation);
                  }}
                  onCropComplete={onCropComplete}
                  onMediaLoaded={(mediaSize) => {
                    setCropperReady(true);
                    setCropperError(false);

                    if (aspect === 'free') {
                      const width = Number(
                        mediaSize?.naturalWidth || mediaSize?.width || 0
                      );
                      const height = Number(
                        mediaSize?.naturalHeight || mediaSize?.height || 0
                      );
                      if (width > 0 && height > 0) {
                        setFreeAspect(
                          Math.min(3, Math.max(0.33, width / height))
                        );
                      }
                    }
                  }}
                  objectFit={activeCropAspect ? 'contain' : 'horizontal-cover'}
                  restrictPosition={!!activeCropAspect}
                  showGrid
                  mediaProps={cropperMediaProps}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.08em] text-theme-muted mb-2">Aspect</p>
                  <div className="flex flex-wrap gap-2">
                    {aspectList.map((a) => (
                      <button
                        key={a.label}
                        type="button"
                        onClick={() => {
                          setPixelCrop(null);
                          setAspect(a.value);
                        }}
                        className={`h-9 px-3 rounded-md text-xs font-semibold border transition-colors ${
                          aspect === a.value
                            ? 'bg-[#002FA7] text-white border-[#002FA7]'
                            : 'border-theme text-theme-muted hover:text-theme-text hover:border-[#002FA7]/50'
                        }`}
                        data-testid={`image-edit-aspect-${a.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {a.label}
                      </button>
                    ))}
                  </div>

                  {aspect === 'free' && (
                    <div
                      className="mt-3 rounded-lg border border-theme bg-theme/30 p-3"
                      data-testid="image-edit-free-crop-controls"
                    >
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <span className="text-[11px] font-semibold text-theme-text">
                          Crop shape
                        </span>
                        <span className="text-[10px] text-theme-muted">
                          {freeAspect.toFixed(2)} : 1
                        </span>
                      </div>

                      <input
                        type="range"
                        min="0.33"
                        max="3"
                        step="0.01"
                        value={freeAspect}
                        onChange={(event) => {
                          setPixelCrop(null);
                          setFreeAspect(Number(event.target.value));
                        }}
                        className="w-full accent-[#002FA7]"
                        aria-label="Free crop aspect ratio"
                        data-testid="image-edit-free-aspect"
                      />

                      <div className="mt-1 flex justify-between text-[9px] text-theme-muted">
                        <span>Portrait</span>
                        <span>Square</span>
                        <span>Landscape</span>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <p className="text-[11px] uppercase tracking-[0.08em] text-theme-muted mb-2">Rotate</p>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setPixelCrop(null);
                        setRotation((r) => r - 90);
                      }}
                      className="h-9 w-10 border-theme p-0"
                      data-testid="image-edit-rotate-left"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setPixelCrop(null);
                        setRotation((r) => r + 90);
                      }}
                      className="h-9 w-10 border-theme p-0"
                      data-testid="image-edit-rotate-right"
                    >
                      <RotateCw className="h-4 w-4" />
                    </Button>
                    <span className="text-xs text-theme-muted tabular-nums ml-0.5">{rotation % 360}°</span>
                  </div>
                </div>

                <div className="col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[11px] uppercase tracking-[0.08em] text-theme-muted">Zoom</p>
                    <span className="text-xs text-theme-muted tabular-nums">{zoom.toFixed(2)}×</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <ZoomOut className="h-4 w-4 text-theme-muted shrink-0" />
                    <input
                      type="range"
                      min="1"
                      max="4"
                      step="0.01"
                      value={zoom}
                      onChange={(e) => {
                        setPixelCrop(null);
                        setZoom(Number(e.target.value));
                      }}
                      className="flex-1 accent-[#002FA7]"
                      data-testid="image-edit-zoom"
                    />
                    <ZoomIn className="h-4 w-4 text-theme-muted shrink-0" />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-theme bg-theme/40 text-sm">
                <div className="flex items-center gap-2 text-theme-muted min-w-0">
                  <ImageIcon className="h-4 w-4 shrink-0" />
                  <span className="truncate">Want to try a different image?</span>
                </div>
                <label className="cursor-pointer shrink-0" data-testid="image-edit-replace-label">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,.heic,.heif"
                    className="hidden"
                    onChange={(e) => handleFilePicked(e.target.files?.[0])}
                  />
                  <span className="h-8 px-3 rounded-md text-xs font-medium border border-theme text-theme-text hover:border-[#002FA7] hover:text-[#002FA7] transition-colors inline-flex items-center">
                    Replace
                  </span>
                </label>
              </div>
            </>
          )}
        </div>

        <div className="px-4 sm:px-5 py-3 border-t border-theme bg-theme/30 flex items-center justify-between gap-3 shrink-0">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange?.(false)}
            disabled={uploading}
            className="text-theme-muted hover:text-theme-text"
            data-testid="image-edit-cancel"
          >
            <X className="h-4 w-4 mr-1.5" />
            Cancel
          </Button>

          <Button
            type="button"
            onClick={handleSave}
            disabled={uploading || !saveReady}
            className="bg-[#002FA7] hover:bg-[#002FA7]/90 text-white min-w-[120px]"
            data-testid="image-edit-save"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Saving…
              </>
            ) : src && !saveReady && !cropperError ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Preparing…
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-1.5" />
                Save image
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
